// Cart Add-ons functionality

window.CartAddons = {
  // Cache for product data to avoid duplicate API calls
  productCache: new Map(),
  
  // Fetch product data including metafields
  fetchProductData: async function(productId) {
    if (this.productCache.has(productId)) {
      return this.productCache.get(productId);
    }
    
    try {
      const response = await fetch(`/products/${productId}.js`);
      const productData = await response.json();
      this.productCache.set(productId, productData);
      return productData;
    } catch (error) {
      console.error('Error fetching product data:', error);
      return null;
    }
  },

  // Normalize product type from either product_type or type field
  getProductType: function(product) {
    return product.product_type || product.type || '';
  },

  // Check if product type is headwear (one-size, exclude from size logic)
  isHeadwear: function(productType) {
    if (!productType) return false;
    const t = productType.toLowerCase().trim();
    return t === 'headwear' || t.includes('cap') || t.includes('hat') || t.includes('beanie');
  },

  // Determine category (tops/bottoms) from product type
  getCategoryFromType: function(productType) {
    if (!productType) return null;
    const t = productType.toLowerCase().trim();
    if (t === 'tops' || t.includes('top') || t.includes('shirt') || t.includes('jacket') || t.includes('hoodie') || t.includes('longsleeve') || t.includes('sweater') || t.includes('vest')) return 'tops';
    if (t === 'bottoms' || t.includes('bottom') || t.includes('short') || t.includes('pant') || t.includes('tight') || t.includes('trouser')) return 'bottoms';
    return null;
  },

  // Standard size order for comparison (larger index = larger size)
  SIZE_ORDER: ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', 'XXXL', '3XL'],

  getLargerSize: function(a, b) {
    const ia = this.SIZE_ORDER.indexOf(a.toUpperCase());
    const ib = this.SIZE_ORDER.indexOf(b.toUpperCase());
    // If both known, return the larger one; if only one known, return that; if neither, return a (first seen)
    if (ia === -1 && ib === -1) return a;
    if (ia === -1) return b;
    if (ib === -1) return a;
    return ib > ia ? b : a;
  },

  // Build a map of category -> preferred size (largest) from cart items, plus a fallback size
  buildSizeMap: function(cartItems) {
    const sizeMap = {};
    const collectedSizes = new Set();
    for (const item of cartItems) {
      const productType = item.product_type || '';
      const handle = item.handle || '';
      const isHw = productType ? this.isHeadwear(productType) : this.isHeadwear(handle);
      if (isHw) continue;
      const sizeOption = item.options_with_values && item.options_with_values.find(o => o.name.toLowerCase() === 'size');
      const size = sizeOption?.value || (item.variant_title && !item.variant_title.includes(' / ') ? item.variant_title : null);
      if (!size) continue;
      collectedSizes.add(size.trim().toUpperCase());
      const category = this.getCategoryFromType(productType) || this.getCategoryFromType(handle);
      if (category) {
        sizeMap[category] = sizeMap[category] ? this.getLargerSize(sizeMap[category], size) : size;
      }
    }
    // Sort collected sizes largest-first so we can try them in priority order
    sizeMap._allSizes = Array.from(collectedSizes).sort((a, b) => {
      const ia = this.SIZE_ORDER.indexOf(a);
      const ib = this.SIZE_ORDER.indexOf(b);
      if (ia === -1 && ib === -1) return 0;
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ib - ia;
    });
    return sizeMap;
  },

  _generationId: 0,

  // Generate grouped addons for all cart items
  generateGroupedAddons: async function(cartItems) {
    const generationId = ++this._generationId;

    const addonsContainer = document.getElementById('cart-addons-container');
    const addonsSection = document.getElementById('cart-addons-section');

    if (!addonsContainer || !addonsSection) return;

    // Clear existing addons
    addonsContainer.innerHTML = '';

    const sizeMap = this.buildSizeMap(cartItems);

    const allAddons = new Map(); // Use Map to avoid duplicates

    // Deduplicate handles — same product in different sizes should only be fetched once
    const uniqueCartItems = cartItems.filter((item, index, arr) =>
      item.handle && arr.findIndex(i => i.handle === item.handle) === index
    );

    try {
      // Fetch all ?view=addons in parallel
      const addonsRawByHandle = await Promise.all(
        uniqueCartItems.map(async (cartItem) => {
          try {
            const response = await fetch(`/products/${cartItem.handle}?view=addons`);
            if (!response.ok) return { cartItem, addonsRaw: null };
            const text = await response.text();
            if (!text || text.trim() === '' || text.trim() === 'null') return { cartItem, addonsRaw: null };
            return { cartItem, addonsRaw: JSON.parse(text) };
          } catch { return { cartItem, addonsRaw: null }; }
        })
      );

      // Collect all unique addon handles to fetch
      const addonHandleSet = new Set();
      for (const { addonsRaw } of addonsRawByHandle) {
        if (addonsRaw) addonsRaw.forEach(a => { if (a.handle) addonHandleSet.add(a.handle); });
      }

      // Fetch all addon product data in parallel, using cache
      const addonProductByHandle = new Map();
      await Promise.all(Array.from(addonHandleSet).map(async (handle) => {
        try {
          const r = await fetch(`/products/${handle}.js`);
          if (r.ok) addonProductByHandle.set(handle, await r.json());
        } catch {}
      }));

      for (const { cartItem, addonsRaw } of addonsRawByHandle) {
        if (!addonsRaw) continue;

        const addonsProducts = addonsRaw
          .map(a => a.handle ? addonProductByHandle.get(a.handle) : null)
          .filter(Boolean);

        // Filter out the current product and already-in-cart items
        const validAddons = addonsProducts.filter(addon =>
          addon.id !== cartItem.product_id &&
          !cartItems.some(item => item.product_id === addon.id)
        );

        // For each addon: determine preferred size and check availability
        for (const addon of validAddons) {
          // Skip if already collected from another cart item
          if (allAddons.has(addon.id)) continue;
          const addonProductType = this.getProductType(addon);

          // Headwear is one-size — show only if available at product level, no size logic
          if (this.isHeadwear(addonProductType)) {
            if (!addon.available) continue;
            allAddons.set(addon.id, { ...addon, sourceItemKey: cartItem.key, preferredSize: null });
            continue;
          }

          // Skip completely sold-out addons
          if (!addon.available) continue;

          const allSizes = sizeMap._allSizes || [];

          if (allSizes.length > 0 && addon.variants && addon.variants.length > 1) {
            // Try each cart size largest-first; use the first one that's available on the addon
            let matchedSize = null;
            for (const sz of allSizes) {
              const sizeVariant = addon.variants.find(v =>
                v.available && (
                  v.title.trim().toUpperCase() === sz ||
                  (v.options && v.options.some(o => o.trim().toUpperCase() === sz)) ||
                  v.title.trim().toUpperCase().split(' / ').includes(sz)
                )
              );
              if (sizeVariant) { matchedSize = sz; break; }
            }
            if (!matchedSize) continue;
            allAddons.set(addon.id, { ...addon, sourceItemKey: cartItem.key, preferredSize: matchedSize });
          } else {
            allAddons.set(addon.id, { ...addon, sourceItemKey: cartItem.key, preferredSize: allSizes[0] || null });
          }
        }
      }

      // Abort if a newer call has already started
      if (generationId !== this._generationId) return;

      // If we have addons, show the section and populate it
      if (allAddons.size > 0) {
        addonsSection.style.display = 'block';

        for (const [addonId, addon] of allAddons) {
          const addonHTML = this.generateAddonItemHTML(addon, addon.sourceItemKey, addon.preferredSize);
          addonsContainer.insertAdjacentHTML('beforeend', addonHTML);
          // Set select value via JS after DOM insertion — HTML attributes alone don't reliably pre-select
          const uniqueFormId = `cart-addon-${addon.id}-${addon.sourceItemKey.replace(/:/g, '-')}`;
          const sel = addonsContainer.querySelector(`#variant-select-${uniqueFormId}-size`);
          if (sel) {
            if (addon.preferredSize) {
              const ps = addon.preferredSize.trim().toUpperCase();
              const matchingOption = Array.from(sel.options).find(o => {
                const ov = o.textContent.trim().toUpperCase();
                return !o.disabled && (ov === ps || ov.split(' / ').includes(ps));
              });
              if (matchingOption) {
                // Value only. The plus deliberately keeps no add handler, see the
                // size-button block in generateAddonItemHTML: a preferred size is
                // shown as a highlighted button, it does not arm a one-tap add.
                sel.value = matchingOption.value;
              }
            } else {
              // No preferred size — force select back to placeholder so browser doesn't auto-pick first option
              sel.value = '';
            }
          }
        }
      } else {
        addonsSection.style.display = 'none';
      }

    } catch (error) {
      console.error('Error generating grouped add-ons:', error);
      addonsSection.style.display = 'none';
    }
  },
  
  // Generate add-ons HTML for a cart item (legacy function for backward compatibility)
  generateAddonsHTML: async function(cartItem) {
    try {
      const productHandle = cartItem.handle;
      if (!productHandle) return '';
      
      // Fetch add-ons data from the custom template
      const response = await fetch(`/products/${productHandle}?view=addons`);
      
      if (!response.ok) {
        console.log(`Template not found for product ${productHandle}, add-ons disabled`);
        return '';
      }
      
      const addonsDataText = await response.text();
      
      if (!addonsDataText || addonsDataText.trim() === '' || addonsDataText.trim() === 'null') return '';
      
      const addonsProducts = JSON.parse(addonsDataText);
      if (!addonsProducts || addonsProducts.length === 0) return '';
      
      // Filter out the current product and sold out items from add-ons
      const validAddons = addonsProducts.filter(addon => 
        addon.id !== cartItem.product_id && addon.available
      );
      if (validAddons.length === 0) return '';
      
      // Generate HTML for add-ons using template
      const addonsContainer = document.createElement('div');
      addonsContainer.className = 'cart-addons';
      
      const title = document.createElement('h4');
      title.textContent = 'Complete your look';
      addonsContainer.appendChild(title);
      
      const grid = document.createElement('div');
      grid.className = 'cart-addons-grid';
      
      for (const addon of validAddons) {
        const addonHTML = this.generateAddonItemHTML(addon, cartItem.key);
        grid.insertAdjacentHTML('beforeend', addonHTML);
      }
      
      addonsContainer.appendChild(grid);
      let addonsHTML = addonsContainer.outerHTML;
      
      return addonsHTML;
      
    } catch (error) {
      console.error('Error generating add-ons HTML:', error);
      return '';
    }
  },
  
  // Generate HTML for individual add-on item using template
  generateAddonItemHTML: function(product, cartItemKey, preferredSize) {
    const template = document.getElementById('cart-addon-item-js-template');
    if (!template) {
      console.error('Cart addon item template not found');
      return '';
    }

    const addonElement = template.content.cloneNode(true);
    return this.populateAddonTemplate(addonElement, product, cartItemKey, preferredSize);
  },

  // Populate addon template with product data
  populateAddonTemplate: function(templateElement, product, cartItemKey, preferredSize) {
    const uniqueFormId = `cart-addon-${product.id}-${cartItemKey.replace(/:/g, '-')}`;
    const hasVariants = product.variants && product.variants.length > 1;

    // Find preferred variant if size is known, else fall back to first available
    let defaultVariant = product.variants ? product.variants.find(v => v.available !== false) || product.variants[0] : null;
    if (preferredSize && product.variants) {
      const ps = preferredSize.trim().toUpperCase();
      const match = product.variants.find(v =>
        v.available !== false && (
          v.title === preferredSize ||
          (v.options && v.options.some(o => o.trim().toUpperCase() === ps)) ||
          (v.title && v.title.toUpperCase().split(' / ').includes(ps))
        )
      );
      if (match) defaultVariant = match;
    }
    const defaultVariantId = defaultVariant ? defaultVariant.id : product.id;
    
    const productUrl = `/products/${product.handle}`;

    // Set product links on image and title
    templateElement.querySelectorAll('.addon-link').forEach(el => el.href = productUrl);

    // Populate image — /products/handle.js returns featured_image as object {src, alt, ...}
    const imageElement = templateElement.querySelector('.addon-image');
    const imageSrc = product.featured_image?.src || (typeof product.featured_image === 'string' ? product.featured_image : null);
    if (imageSrc && imageElement) {
      imageElement.src = imageSrc;
      imageElement.alt = product.title;
      imageElement.classList.remove('hidden');
    }

    // Populate title and price — price from /products/handle.js is already in cents
    const titleElement = templateElement.querySelector('.addon-title');
    const priceElement = templateElement.querySelector('.addon-price');

    if (titleElement) titleElement.textContent = product.title;
    if (priceElement) priceElement.textContent = this.formatMoney(product.price);

    // Handle variants and buttons
    if (hasVariants) {
      const variantsContainer = templateElement.querySelector('.addon-variants');
      const noVariantsContainer = templateElement.querySelector('.addon-no-variants');
      const variantSelect = templateElement.querySelector('.variant-selector');

      // Check if any variant is available
      const hasAvailableVariant = product.variants.some(v => v.available);

      if (variantsContainer && variantSelect) {
        variantsContainer.classList.remove('hidden');
        if (noVariantsContainer) noVariantsContainer.classList.add('hidden');

        variantsContainer.setAttribute('data-form-id', uniqueFormId);

        variantSelect.setAttribute('data-option-name', 'Size');
        variantSelect.setAttribute('data-form-id', uniqueFormId);
        variantSelect.id = `variant-select-${uniqueFormId}-size`;

        // Remove all existing options including placeholder
        variantSelect.innerHTML = '';

        let preselectedId = null;
        const ps = preferredSize ? preferredSize.trim().toUpperCase() : null;

        // Placeholder added after variants if nothing matched, to avoid browser auto-selecting first option

        // Add variant options
        product.variants.forEach(variant => {
          const option = document.createElement('option');
          option.value = variant.id;
          option.setAttribute('data-variant-id', variant.id);
          option.textContent = variant.title + (variant.available ? '' : ' - Sold out');

          if (!variant.available) {
            option.disabled = true;
          }

            // Pre-select preferred size
          if (ps && variant.available) {
            const vt = variant.title.trim().toUpperCase();
            const titleMatch = vt === ps;
            const optionsMatch = variant.options && variant.options.some(o => o.trim().toUpperCase() === ps);
            const partMatch = vt.split(' / ').includes(ps);
            if (titleMatch || optionsMatch || partMatch) {
              option.setAttribute('selected', 'selected');
              preselectedId = variant.id;
            }
          }

          variantSelect.appendChild(option);
        });

        // Always prepend a placeholder; remove it only if a size was pre-selected
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'size';
        placeholder.disabled = true;
        placeholder.selected = !preselectedId;
        variantSelect.insertBefore(placeholder, variantSelect.firstChild);

        // Individual size buttons, the same control the collection cards use.
        // Built from the same variant list that fills the select above; the select
        // stays as the value holder the rest of this file reads.
        const sizesContainer = templateElement.querySelector('.cart-addon-sizes');
        if (sizesContainer) {
          sizesContainer.innerHTML = '';
          sizesContainer.dataset.formId = uniqueFormId;
          sizesContainer.dataset.productTitle = product.title;

          product.variants.forEach(variant => {
            const sizeButton = document.createElement('button');
            sizeButton.type = 'button';
            sizeButton.className = 'cart-addon-size-button';
            sizeButton.dataset.variantId = variant.id;
            sizeButton.dataset.available = String(Boolean(variant.available));
            // Last option is the size on a multi-option product; title alone would
            // read "Black / M" and blow the button out of the panel.
            sizeButton.textContent = (variant.options && variant.options.length)
              ? variant.options[variant.options.length - 1]
              : variant.title;
            // The size already in the cart, marked rather than silently applied,
            // so the customer sees which one we guessed before they commit to it.
            if (preselectedId && String(variant.id) === String(preselectedId)) {
              sizeButton.dataset.preferred = 'true';
            }
            sizesContainer.appendChild(sizeButton);
          });
        }

        // Handle add button in variants container
        const variantAddButton = variantsContainer.querySelector('.addon-add-btn');
        const variantSoldOutSpan = variantsContainer.querySelector('.addon-sold-out');

        if (hasAvailableVariant && variantAddButton) {
          const selectedOpt = variantSelect.querySelector('option:checked:not([disabled])');
          const initialVariantId = (selectedOpt && selectedOpt.value) ? selectedOpt.value : defaultVariantId;
          // No onclick and no data-requires-variant on the plus any more: on a
          // product with sizes the plus only opens the panel, and the size button
          // the customer taps carries the variant. Leaving an add handler here
          // would add whatever size happened to be preselected on the first tap,
          // before the panel had even been read.
          variantAddButton.classList.remove('hidden');
          if (variantSoldOutSpan) variantSoldOutSpan.classList.add('hidden');
        } else {
          if (variantAddButton) variantAddButton.classList.add('hidden');
          if (variantSoldOutSpan) variantSoldOutSpan.classList.remove('hidden');
        }
      }
      
      // Add variants data script
      const variantsDataScript = templateElement.querySelector('.addon-variants-data');
      if (variantsDataScript) {
        variantsDataScript.id = `product-variants-${uniqueFormId}`;
        variantsDataScript.textContent = JSON.stringify(product.variants);
      }
    } else {
      // Product has no variants, use the no-variants container
      const variantsContainer = templateElement.querySelector('.addon-variants');
      const noVariantsContainer = templateElement.querySelector('.addon-no-variants');
      
      if (variantsContainer) variantsContainer.classList.add('hidden');
      if (noVariantsContainer) noVariantsContainer.classList.remove('hidden');
      
      // Handle add button in no-variants container
      const noVariantAddButton = noVariantsContainer.querySelector('.addon-add-btn');
      const noVariantSoldOutSpan = noVariantsContainer.querySelector('.addon-sold-out');
      
      if (product.available && noVariantAddButton) {
        noVariantAddButton.setAttribute('onclick', `handleCartAddonAdd('${uniqueFormId}', ${defaultVariantId}, '${product.title.replace(/'/g, "\\'")}', event)`);
        noVariantAddButton.classList.remove('hidden');
        if (noVariantSoldOutSpan) noVariantSoldOutSpan.classList.add('hidden');
      } else {
        if (noVariantAddButton) noVariantAddButton.classList.add('hidden');
        if (noVariantSoldOutSpan) noVariantSoldOutSpan.classList.remove('hidden');
      }
    }
    
    // Return the populated template as HTML string
    const tempDiv = document.createElement('div');
    tempDiv.appendChild(templateElement);
    return tempDiv.innerHTML;
  },
  
  // Simple money formatting (assumes Shopify.formatMoney is available)
  formatMoney: function(price) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(price);
    }
    return `$${(price / 100).toFixed(2)}`;
  },
  // Handle variant selection for add-ons
  handleVariantSelection: function(formId) {
    const cleanFormId = formId.replace(/:/g, '-');
    const selector = document.querySelector(`#variant-select-${cleanFormId}-size`);
    if (!selector) return null;
    
    const selectedOption = selector.options[selector.selectedIndex];
    if (!selectedOption || !selectedOption.value) return null;
    
    // If variant ID is directly available, use it
    if (selectedOption.dataset.variantId) {
      return selectedOption.dataset.variantId;
    }
    
    // Otherwise, find variant ID from product variants data
    const variantsScript = document.querySelector(`#product-variants-${cleanFormId}`);
    if (variantsScript) {
      try {
        const variants = JSON.parse(variantsScript.textContent);
        const selectedVariant = variants.find(variant => 
          variant.title === selectedOption.value || 
          variant.options.includes(selectedOption.value)
        );
        return selectedVariant ? selectedVariant.id : null;
      } catch (error) {
        console.error('Error parsing variants data:', error);
      }
    }
    
    return null;
  },

  // Add to cart handler for cart add-ons
  addToCart: function(formId, defaultVariantId, productTitle, event) {
    event.preventDefault();
    event.stopPropagation();
    
    const button = event.target;
    const requiresVariant = button.dataset.requiresVariant === 'true';
    
    let selectedVariantId = defaultVariantId;
    
    // If product has multiple variants, get selected variant
    if (requiresVariant) {
      selectedVariantId = this.handleVariantSelection(formId);
      
      if (!selectedVariantId) {
        // Show validation message
        const cleanFormId = formId.replace(/:/g, '-');
        const selector = document.querySelector(`#variant-select-${cleanFormId}-size`);
        if (selector) {
          selector.style.borderColor = 'red';
          setTimeout(() => {
            selector.style.borderColor = '';
          }, 2000);
        }
        return;
      }
    }
    
    // Set loading state
    const originalText = button.textContent;
    button.textContent = '...';
    button.disabled = true;
    
    // Add to cart
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: selectedVariantId,
        quantity: 1
      })
    })
    .then(response => response.json())
    .then(data => {
      console.log('Add-on product added to cart:', data);
      
      // Update cart contents
      return fetch('/cart.js');
    })
    .then(response => response.json())
    .then(async cart => {
      // Use centralized cart update function for consistency
      if (window.updateCart) {
        // Use the main updateCart function which handles all updates including preorder messages
        await window.updateCart(cart);
      } else {
        // Fallback to individual functions if updateCart is not available
        if (window.updateMenuCart) {
          window.updateMenuCart(cart);
        }
        if (window.updateMainCart) {
          window.updateMainCart(cart);
        }
        if (window.updateCartCount) {
          window.updateCartCount(cart.item_count);
        }
        // Update free shipping progress
        if (window.updateFreeShippingProgress) {
          window.updateFreeShippingProgress(cart);
        }
      }
      
      // Show success feedback
      button.textContent = '✓';
      button.classList.add('bg-green-600');
      
      // Scroll to top of cart drawer
      const cartDrawerScrollContainer = document.querySelector('#cart-menu .flex-1.overflow-y-auto');
      if (cartDrawerScrollContainer) {
        cartDrawerScrollContainer.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
      
      setTimeout(() => {
        button.textContent = originalText;
        button.classList.remove('bg-green-600');
        button.disabled = false;
      }, 1500);
      
    })
    .catch(error => {
      console.error('Error adding add-on to cart:', error);
      button.textContent = originalText;
      button.disabled = false;
      
      // Show error feedback
      button.textContent = 'Error';
      button.classList.add('bg-red-600');
      
      setTimeout(() => {
        button.textContent = originalText;
        button.classList.remove('bg-red-600');
      }, 2000);
    });
  }
};

// Make it globally available with proper context binding
window.handleCartAddonAdd = function(formId, defaultVariantId, productTitle, event) {
  return window.CartAddons.addToCart(formId, defaultVariantId, productTitle, event);
};

// Quick add for the "complete your look" rows, driven by CLICK throughout.
// The collection cards reveal their size picker with group-hover, which simply
// does not fire on a touch device — copying that here would have made the quick
// add unusable on a phone, which is where most of this drawer gets used.
//
// Delegated from the document because the rows are cloned in and replaced on
// every cart update; a listener bound to the buttons themselves would be thrown
// away with them.
document.addEventListener('click', function(event) {
  const sizeButton = event.target.closest('.cart-addon-size-button');
  if (sizeButton) {
    event.preventDefault();
    event.stopPropagation();

    if (sizeButton.dataset.available === 'false') return;

    const sizes = sizeButton.closest('.cart-addon-sizes');
    if (!sizes || !sizes.dataset.formId) return;

    // addToCart uses the id passed in whenever the clicked control carries no
    // data-requires-variant, and these buttons carry none. So the size the
    // customer touched is the size that gets added, with no select to consult.
    window.CartAddons.addToCart(
      sizes.dataset.formId,
      sizeButton.dataset.variantId,
      sizes.dataset.productTitle || '',
      event
    );

    // Close it behind them. The row is usually re-rendered by the cart update
    // anyway, but not always, and a panel left hanging open over the next row
    // looks like the tap did not register.
    const controls = sizeButton.closest('.cart-addon-controls');
    if (controls) {
      controls.classList.remove('is-open');
      const toggle = controls.querySelector('.cart-addon-add');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }
    return;
  }

  // The plus opens its own panel and closes every other one, so two panels can
  // never overlap in a narrow drawer.
  const plus = event.target.closest('.addon-variants .cart-addon-add');
  const openPanel = plus ? plus.closest('.cart-addon-controls') : null;

  document.querySelectorAll('.cart-addon-controls.is-open').forEach(function(controls) {
    if (controls === openPanel) return;
    controls.classList.remove('is-open');
    const toggle = controls.querySelector('.cart-addon-add');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  });

  if (!plus || !openPanel) return;

  event.preventDefault();
  event.stopPropagation();
  const nowOpen = openPanel.classList.toggle('is-open');
  plus.setAttribute('aria-expanded', String(nowOpen));
}); 